// Recover current-page reads through their normal Redux paths, never by
// replaying stored HTTP parameters (which may belong to an old selection).
export const READ_TIMEOUT_MS = 20000;

export const getReadScope = (config) => {
  if ((config?.method || 'get').toLowerCase() !== 'get') return null;
  const url = config?.url || '';
  if (
    /^\/v2\/restaurants\/\d+\/site$/.test(url) ||
    /^\/restaurant\/customer\/menu\/(asap|target)$/.test(url)
  )
    return 'restaurant';
  if (url === '/consumer/customer/restaurants') return 'restaurants';
  return null;
};

export const createReadRecovery = () => {
  const listeners = new Set();
  const handlers = new Map();
  const active = new Map();
  const latest = new Map();
  const failures = new Map();
  const automaticAttempts = new Set();
  let serial = 0;
  let pending = false;
  let deadline;
  let resumeTimer;
  let snapshot = { offline: false, failed: false, retrying: false };

  const publish = () => {
    const next = {
      offline: navigator.onLine === false,
      failed: failures.size > 0,
      retrying: pending && failures.size > 0,
    };
    if (JSON.stringify(next) !== JSON.stringify(snapshot)) {
      snapshot = next;
      listeners.forEach((listener) => listener());
    }
  };
  const retry = (automatic = false) => {
    if (
      document.visibilityState !== 'visible' ||
      navigator.onLine === false ||
      pending
    )
      return false;
    const scopes = [...new Set(failures.values())].filter(
      (scope) =>
        handlers.has(scope) &&
        ![...active.values()].some((read) => read.scope === scope) &&
        (!automatic || !automaticAttempts.has(scope))
    );
    if (!scopes.length) return false;
    pending = true;
    scopes.forEach((scope) => automaticAttempts.add(scope));
    publish();
    // A page can legitimately decline a read while timing/details reconcile.
    // Never leave its Retry button disabled indefinitely.
    clearTimeout(deadline);
    deadline = setTimeout(() => {
      pending = false;
      publish();
    }, READ_TIMEOUT_MS + 1000);
    scopes.forEach((scope) => handlers.get(scope)?.());
    return true;
  };
  return {
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    register(scope, callback) {
      handlers.set(scope, callback);
      return () => {
        if (handlers.get(scope) !== callback) return;
        handlers.delete(scope);
        failures.forEach((value, key) => {
          if (value === scope) failures.delete(key);
        });
        automaticAttempts.delete(scope);
        if (!failures.size) {
          pending = false;
          clearTimeout(deadline);
        }
        publish();
      };
    },
    begin(config) {
      const scope = getReadScope(config);
      if (!scope || !handlers.has(scope)) return config;
      serial += 1;
      // Axios carries these private transport fields back on success/error.
      const id = serial;
      active.set(id, { scope, url: config.url, owner: handlers.get(scope) });
      latest.set(config.url, id);
      return {
        ...config,
        timeout: config.timeout || READ_TIMEOUT_MS,
        readRecoveryId: id,
      };
    },
    finish(config, error) {
      const id = config?.readRecoveryId;
      const read = active.get(id);
      active.delete(id);
      if (
        !read ||
        read.owner !== handlers.get(read.scope) ||
        latest.get(read.url) !== id
      )
        return;
      const recoverable =
        error &&
        (['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT'].includes(error.code) ||
          (!error.response && !!error.request) ||
          error.response?.status >= 500);
      if (error?.code === 'ERR_CANCELED') return;
      if (recoverable) failures.set(read.url, read.scope);
      else failures.delete(read.url);
      if (!failures.size) automaticAttempts.clear();
      if (!active.size) {
        pending = false;
        clearTimeout(deadline);
      }
      publish();
    },
    retry: () => retry(false),
    start() {
      const onResume = () => {
        publish();
        clearTimeout(resumeTimer);
        resumeTimer = setTimeout(() => retry(true), 250);
      };
      const onOffline = () => {
        automaticAttempts.clear();
        publish();
      };
      window.addEventListener('online', onResume);
      window.addEventListener('offline', onOffline);
      document.addEventListener('visibilitychange', onResume);
      publish();
      return () => {
        window.removeEventListener('online', onResume);
        window.removeEventListener('offline', onOffline);
        document.removeEventListener('visibilitychange', onResume);
        clearTimeout(resumeTimer);
        clearTimeout(deadline);
        pending = false;
      };
    },
  };
};

export const readRecovery = createReadRecovery();
